'use client';

import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import ProfessionalDialog from '@/components/common/professional-dialog';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Trash as TrashIcon } from '@phosphor-icons/react/dist/ssr/Trash';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z as zod } from 'zod';

import { logger } from '@/lib/default-logger';
import { dayjs } from '@/lib/v2/dayjs';

import type { Event, EventPriority } from './types';

export interface EventCreateParams {
  title: string;
  description?: string;
  allDay: boolean;
  start: Date;
  end: Date;
  priority?: EventPriority;
  type?: string;
  visibility?: 'public' | 'private';
  isRecurring?: boolean;
  recurrenceType?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrenceInterval?: number;
  recurrenceDays?: number[];
  recurrenceEndDate?: Date;
  recurrenceCount?: number;
}

export interface EventUpdateParams {
  title?: string;
  description?: string;
  start?: Date;
  end?: Date;
  allDay?: boolean;
  priority?: EventPriority;
  type?: string;
  visibility?: 'public' | 'private';
  isRecurring?: boolean;
  recurrenceType?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  recurrenceInterval?: number;
  recurrenceDays?: number[];
  recurrenceEndDate?: Date;
  recurrenceCount?: number;
}

const baseSchema = zod
  .object({
    title: zod.string().min(1, 'Title is required').max(255),
    description: zod.string().max(5000).optional().or(zod.literal('')),
    start: zod.date({ required_error: 'Start date is required' }),
    end: zod.date({ required_error: 'End date is required' }),
    allDay: zod.boolean(),
    type: zod.string().optional(),
    visibility: zod.enum(['public', 'private']).optional(),
    isRecurring: zod.boolean().optional(),
    recurrenceType: zod.enum(['daily', 'weekly', 'monthly', 'yearly']).optional(),
    recurrenceInterval: zod.coerce.number().min(1).max(100).optional().catch(undefined),
    recurrenceEndDate: zod.date().optional().nullable(),
    recurrenceCount: zod.coerce.number().min(1).max(365).optional().nullable().catch(null),
  })
  .refine((data) => data.start < data.end, {
    message: 'End must be after start',
    path: ['end'],
  })
  .refine((data) => {
    if (data.isRecurring && !data.recurrenceType) return false;
    return true;
  }, {
    message: 'Select how often this repeats',
    path: ['recurrenceType'],
  })
  .refine((data) => {
    if (data.isRecurring && !data.recurrenceEndDate && !data.recurrenceCount) return false;
    return true;
  }, {
    message: 'Set an end date or number of occurrences',
    path: ['recurrenceEndDate'],
  });

function getSchema(_action: 'create' | 'update') {
  // Events may be dated in the past (Google Calendar / Outlook behaviour), so no
  // "start cannot be in the past" refinement — it also rejected the form when the
  // dialog's pre-filled "now" passed while the user was still filling it in.
  return baseSchema;
}

type Values = zod.infer<typeof baseSchema>;

function getDefaultValues({ event, range }: { event?: Event; range?: { start: Date; end: Date } }): Values {
  if (event) {
    return {
      title: event.title ?? '',
      description: event.description ?? '',
      start: event.start ?? new Date(),
      end: event.end ?? dayjs().add(1, 'hour').toDate(),
      allDay: event.allDay ?? false,
      type: event.type ?? 'Community Event',
      visibility: event.visibility ?? 'public',
      isRecurring: event.isRecurring ?? false,
      recurrenceType: event.recurrenceType ?? 'weekly',
      recurrenceInterval: event.recurrenceInterval ?? 1,
      recurrenceEndDate: event.recurrenceEndDate ?? null,
      recurrenceCount: event.recurrenceCount ?? null,
    };
  }

  const start = range?.start ?? new Date();
  const end = range?.end ?? dayjs(start).add(1, 'hour').toDate();

  return {
    title: '',
    description: '',
    start,
    end,
    allDay: false,
    type: 'Community Event',
    visibility: 'public',
    isRecurring: false,
    recurrenceType: 'weekly',
    recurrenceInterval: 1,
    recurrenceEndDate: null,
    recurrenceCount: null,
  };
}

export interface EventDialogProps {
  action: 'create' | 'update';
  event?: Event;
  onClose?: () => void;
  onCreate?: (params: EventCreateParams) => void | Promise<unknown>;
  onDelete?: (eventId: string) => void;
  onUpdate?: (eventId: string, params: EventUpdateParams) => void | Promise<unknown>;
  open?: boolean;
  range?: { start: Date; end: Date };
}

export function EventDialog({
  action = 'create',
  event,
  onClose,
  onCreate,
  onDelete,
  onUpdate,
  open = false,
  range,
}: EventDialogProps): React.JSX.Element {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Values>({ defaultValues: getDefaultValues({ event, range }), resolver: zodResolver(getSchema(action)) });

  const isRecurring = useWatch({ control, name: 'isRecurring' });
  const allDay = useWatch({ control, name: 'allDay' });

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      try {
        const params: EventCreateParams = {
          title: values.title.trim(),
          description: values.description?.trim() || '',
          end: values.end,
          start: values.start,
          allDay: values.allDay,
          type: values.type || undefined,
          visibility: values.visibility,
          ...(values.isRecurring ? {
            isRecurring: true,
            recurrenceType: values.recurrenceType,
            recurrenceInterval: values.recurrenceInterval,
            recurrenceEndDate: values.recurrenceEndDate,
            recurrenceCount: values.recurrenceCount,
          } : {}),
        };

        // Await the mutation so the dialog only closes on success — otherwise a
        // failed save discarded everything the user had typed.
        if (action === 'update') {
          await onUpdate?.(event!.id, params);
        } else {
          await onCreate?.(params);
        }
        onClose?.();
      } catch (err) {
        // The mutation's onError already toasted the API's reason; keep the
        // dialog open so the user can correct and resubmit.
        logger.error(err);
      }
    },
    [action, event, onCreate, onUpdate, onClose]
  );

  React.useEffect(() => {
    reset(getDefaultValues({ event, range }));
  }, [event, range, reset]);

  return (
    <ProfessionalDialog
      actions={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          {action === 'update' ? (
            <IconButton color="error" onClick={() => onDelete?.(event!.id)}>
              <TrashIcon />
            </IconButton>
          ) : (
            <span />
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button color="secondary" onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} type="submit" variant="contained">
              {action === 'update' ? 'Save Changes' : 'Create Event'}
            </Button>
          </Stack>
        </Stack>
      }
      maxWidth="sm"
      onClose={onClose || (() => {})}
      open={Boolean(open)}
      subtitle={event ? 'Update event details' : 'Add a new calendar event'}
      title={event ? 'Edit Event' : 'New Event'}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3.5}>

          {/* Title */}
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <FormControl error={Boolean(errors.title)} fullWidth>
                <OutlinedInput
                  {...field}
                  placeholder="Event title"
                  sx={{ fontWeight: 600, fontSize: '1.1rem' }}
                />
                {errors.title ? <FormHelperText>{errors.title.message}</FormHelperText> : null}
              </FormControl>
            )}
          />

          {/* Description */}
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <FormControl error={Boolean(errors.description)} fullWidth>
                <OutlinedInput
                  {...field}
                  multiline
                  rows={3}
                  placeholder="Add a description (optional)"
                />
                {errors.description ? <FormHelperText>{errors.description.message}</FormHelperText> : null}
              </FormControl>
            )}
          />

          <Divider />

          {/* Date & Time */}
          <Stack spacing={2.5}>
            {/* All-day + Visibility toggles */}
            <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
              <Controller
                control={control}
                name="allDay"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} size="small" />}
                    label="All day"
                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, fontSize: '0.9rem' } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="visibility"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value === 'private'}
                        onChange={(e) => field.onChange(e.target.checked ? 'private' : 'public')}
                        size="small"
                      />
                    }
                    label="Managers only"
                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, fontSize: '0.9rem' } }}
                  />
                )}
              />
            </Stack>

            {/* Start */}
            <Stack direction="row" spacing={2}>
              <Controller
                control={control}
                name="start"
                render={({ field }) => (
                  <DatePicker
                    format="MMM D, YYYY"
                    label="Start date"
                    onChange={(date) => {
                      if (date) {
                        const t = dayjs(field.value);
                        field.onChange(dayjs(date).hour(t.hour()).minute(t.minute()).toDate());
                      }
                    }}
                    slotProps={{ textField: { error: Boolean(errors.start), helperText: errors.start?.message, fullWidth: true, size: 'small' } }}
                    value={dayjs(field.value)}
                    sx={{ flex: 1 }}
                  />
                )}
              />
              {!allDay && (
                <Controller
                  control={control}
                  name="start"
                  render={({ field }) => {
                    const startDayjs = dayjs(field.value);
                    const isToday = startDayjs.isSame(dayjs(), 'day');
                    return (
                      <TimePicker
                        format="hh:mm A"
                        label="Start time"
                        onChange={(time) => {
                          if (time) {
                            const d = dayjs(field.value);
                            field.onChange(d.hour(dayjs(time).hour()).minute(dayjs(time).minute()).toDate());
                          }
                        }}
                        slotProps={{ textField: { error: Boolean(errors.start), fullWidth: true, size: 'small' } }}
                        value={dayjs(field.value)}
                        sx={{ flex: 1 }}
                      />
                    );
                  }}
                />
              )}
            </Stack>

            {/* End */}
            <Stack direction="row" spacing={2}>
              <Controller
                control={control}
                name="end"
                render={({ field }) => (
                  <DatePicker
                    format="MMM D, YYYY"
                    label="End date"
                    onChange={(date) => {
                      if (date) {
                        const t = dayjs(field.value);
                        field.onChange(dayjs(date).hour(t.hour()).minute(t.minute()).toDate());
                      }
                    }}
                    slotProps={{ textField: { error: Boolean(errors.end), helperText: errors.end?.message, fullWidth: true, size: 'small' } }}
                    value={dayjs(field.value)}
                    sx={{ flex: 1 }}
                  />
                )}
              />
              {!allDay && (
                <Controller
                  control={control}
                  name="end"
                  render={({ field }) => (
                    <TimePicker
                      format="hh:mm A"
                      label="End time"
                      onChange={(time) => {
                        if (time) {
                          const d = dayjs(field.value);
                          field.onChange(d.hour(dayjs(time).hour()).minute(dayjs(time).minute()).toDate());
                        }
                      }}
                      slotProps={{ textField: { error: Boolean(errors.end), fullWidth: true, size: 'small' } }}
                      value={dayjs(field.value)}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              )}
            </Stack>
          </Stack>

          <Divider />

          {/* Recurring */}
          <Stack spacing={2}>
            <Controller
              control={control}
              name="isRecurring"
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} size="small" />}
                  label="Repeating event"
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.9rem' } }}
                />
              )}
            />

            {isRecurring && (
              <Stack spacing={2.5} sx={{ pl: 1 }}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Repeat every
                  </Typography>
                  <Controller
                    control={control}
                    name="recurrenceInterval"
                    render={({ field }) => (
                      <Select {...field} size="small" sx={{ minWidth: 70 }}>
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                          <MenuItem key={n} value={n}>{n}</MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <Controller
                    control={control}
                    name="recurrenceType"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.recurrenceType)} size="small" sx={{ minWidth: 120 }}>
                        <Select {...field}>
                          <MenuItem value="daily">day(s)</MenuItem>
                          <MenuItem value="weekly">week(s)</MenuItem>
                          <MenuItem value="monthly">month(s)</MenuItem>
                          <MenuItem value="yearly">year(s)</MenuItem>
                        </Select>
                        {errors.recurrenceType ? <FormHelperText>{errors.recurrenceType.message}</FormHelperText> : null}
                      </FormControl>
                    )}
                  />
                </Stack>

                {/* End condition */}
                <Stack spacing={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Until
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Controller
                      control={control}
                      name="recurrenceEndDate"
                      render={({ field }) => (
                        <DatePicker
                          format="MMM D, YYYY"
                          label="End date"
                          onChange={(date) => field.onChange(date ? date.toDate() : null)}
                          slotProps={{
                            textField: {
                              error: Boolean(errors.recurrenceEndDate),
                              helperText: errors.recurrenceEndDate?.message,
                              fullWidth: true,
                              size: 'small',
                            },
                          }}
                          value={field.value ? dayjs(field.value) : null}
                          sx={{ flex: 1 }}
                        />
                      )}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      or
                    </Typography>
                    <Controller
                      control={control}
                      name="recurrenceCount"
                      render={({ field }) => (
                        <OutlinedInput
                          {...field}
                          type="number"
                          placeholder="# times"
                          size="small"
                          inputProps={{ min: 1, max: 365 }}
                          sx={{ flex: 0.6 }}
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>

        </Stack>
      </form>
    </ProfessionalDialog>
  );
}
